/**
 * Kanonik HTTP istemcisi — MIMARI.md'nin packages/api paketi (FW-14 KAPSAM D).
 *
 * Kimlik cerezle tasinir: her istek `credentials: 'include'` ile gonderilir,
 * oturum cerezi (`fw-session`, httpOnly — sunucu tarafinda Set-Cookie ile
 * yazilir) tarayici JS'ine HIC gorunmez. Bu istemci bir token'i asla okumaz/
 * yazmaz; tarayici depolamasina (kalici anahtar-deger depolari) hic
 * YAZILMAZ (bkz. FW-14 kabul kriteri 3).
 *
 * `fetch` KULLANILIR, axios DEGIL: yigin bagimliligi tek noktadan
 * (packages/shell) yonetme karari (FW-14 KAPSAM B) axios'u burada YENIDEN
 * eklemeyi anlamsiz kilar — native fetch her tarayicida ve Node 20'de
 * hazir.
 *
 * NOT: repoda ayrica packages/core icinde axios tabanli eski bir istemci
 * var (miras). Ikisinin birlestirilmesi FW-14'un kabul kriteri DISINDADIR
 * ve insan karari gerektirir (bkz. FW-14 yorumu) — bu paket ondan HICBIR
 * SEY import ETMEZ.
 */
export interface ApiClientOptions {
  readonly baseUrl: string
  readonly tenantId?: string
}

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly headers?: Record<string, string>
  readonly body?: unknown
  readonly signal?: AbortSignal
  /** Binary endpoints opt in; JSON remains the default for all existing callers. */
  readonly responseType?: 'json' | 'blob'
  /**
   * Varsayılan ham DTO'dur. Yalnız doğrulanmış Result uçlarında seçilir:
   * Status.OK için data döner, semantik hata ApiError olarak fırlatılır.
   */
  readonly responseContract?: 'appkit-result'
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * FW-129 OLCULDU BULGU (id-stg.fusapp.cloud'a canli bir cagri, register/
     * verify-otp'nin yanlis/tukenmis/olu-jeton dallarini AYIRT ETMEK icin) —
     * HTTP hata kolunda `message` yalniz durum bilgisidir (asagidaki
     * `request()`e bak), sunucunun govdesindeki upstream hata KODUNU ASLA
     * TASIMAZ. `code` bu bosluk icin EKLENDI: govde `{ error: 'X', ... }`
     * seklindeyse (bu depoda zaten kurulu bir sozlesme — bkz.
     * invite-flow.spec.ts'in `IDENTITY_REGISTRATION_INVITATION_INVALID`
     * mock'u) `X` buraya yazilir; govde JSON degilse/bu alani tasimiyorsa
     * `undefined` kalir. TAMAMEN OPSIYONEL — mevcut hicbir cagiran (yalniz
     * `status`e veya `message`e bakanlar) BOZULMAZ.
     */
    readonly code?: string,
    /** Sunucunun açıklaması, yalnız metin olarak kullanılmalı; HTML olarak render edilmez. */
    readonly description?: string,
    /** Semantik Result durumu; status her zaman gerçek HTTP durumudur. */
    readonly resultStatus?: number,
    /** Explicit server capability metadata; no raw error body is retained. */
    readonly requiredCapability?: string,
    readonly source?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiClient {
  readonly baseUrl: string
  readonly tenantId?: string
  request<T>(path: string, options?: ApiRequestOptions): Promise<T>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

/**
 * OrganizationController {error,error_description}; ResultFilter ProblemDetails
 * {errorCode,detail} döner. Boş Forbid/HTML proxy yanıtını okuyamamak HTTP hata
 * durumunu kaybettirmez. Açıklama message'a eklenmez: mevcut i18n tüketicileri
 * code/status seçimini korur, sunucu metni yalnız description'da taşınır.
 */
async function extractErrorDetails(response: Response): Promise<{ code?: string; description?: string; requiredCapability?: string; source?: string }> {
  try {
    const body: unknown = await response.json()
    if (isRecord(body)) {
      return {
        code: readString(body.error) ?? readString(body.errorCode),
        requiredCapability: readString(body.requiredCapability),
        source: readString(body.source),
        description: readString(body.error_description) ?? readString(body.detail),
      }
    }
  } catch {
    // Boş veya JSON olmayan hata gövdesi: status/message ile devam edilir.
  }
  return {}
}

/**
 * AppKit.Core.Result<T>: sayısal status + httpStatus + validationErrors + data.
 * Result.Success() HttpStatus'u atamaz (0); başarıyı yalnız Status.OK=200 söyler.
 * Fusapp ResultFilter başarısız MVC Result'larını HTTP hatasına çevirir; mevcut
 * Organization uçları ise zaten ham DTO döner. Bu yüzden otomatik zarf tahmini
 * YOK: yalnız responseContract ile seçilen uçta data açılır veya hata atılır.
 */
function adaptAppKitResult(body: unknown, httpStatus: number): unknown {
  if (!isRecord(body) || typeof body.status !== 'number' || !Number.isInteger(body.status)
    || typeof body.httpStatus !== 'number' || !Number.isInteger(body.httpStatus)
    || !isRecord(body.validationErrors)) {
    throw new ApiError('Invalid AppKit Result response', httpStatus, 'INVALID_RESPONSE')
  }
  if (body.status !== 200) {
    throw new ApiError('API result failed', httpStatus,
      readString(body.errorCode), readString(body.errorMessage), body.status)
  }
  return body.data
}

function buildHeaders(tenantId: string | undefined, extra: Record<string, string> | undefined, multipart = false, binary = false): Headers {
  const headers = new Headers(extra)
  if (!binary || !headers.has('Accept')) headers.set('Accept', binary ? '*/*' : 'application/json')
  // Fetch owns the multipart boundary, including when a caller reuses JSON headers.
  if (multipart) headers.delete('Content-Type')
  else if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (tenantId) headers.set('X-Tenant-Id', tenantId)
  return headers
}

/**
 * FW-126 OLCULDU DUZELTME: `new URL(path, baseUrl)` yalniz `baseUrl` MUTLAK
 * bir URL string'iyse calisir (WHATWG URL spec — base string ctor'a
 * dokumanin konumuna gore COZULMEZ, oldugu gibi ayristirilir). Her yuzeyin
 * main.ts'i `apiClientOptions: { baseUrl: '/' }` verir (gorece); `new
 * URL('/api/...', '/')` `TypeError: Invalid URL` FIRLATIR. FW-14'un kendi
 * testi (create-api-client.spec.ts) bunu hic yakalamadi cunku YALNIZ mutlak
 * baseUrl ile test edildi — hicbir yuzey `request()`'i gercekten cagirmiyordu.
 * FW-126 (apps/auth) bunu cagiran ILK yuzey; canli tarayicida ilk submit'te
 * patlardi. Cozum: gorece bir baseUrl, calisma aninin origin'ine gore
 * cozulur (tarayicida `window.location.origin`).
 *
 * FW-console-entra-giris OLCULDU IKINCI DUZELTME (canli tarayici kaniti
 * sirasinda yakalandi, create-api-client.spec.ts'te BENZER bir bosluk vardi —
 * yalniz `baseUrl: '/'` test edilmisti, KENDI YOLU olan gorece bir baseUrl
 * ('/web-api' gibi, apps/auth+apps/console'un YEREL dev proxy'si) HIC
 * DEGIL): `new URL(path, new URL(baseUrl, origin))` — `path` '/' ile
 * basladiginda (her cagrida boyle, bkz. useSession.ts vb.) WHATWG URL
 * cozumlemesi base'in TUM YOLUNU SILER ("absolute-path reference" semantigi,
 * RFC 3986 §5.3) — yani `/web-api` oneki SESSIZCE dusuyordu, istek dogrudan
 * origin + path'e gidiyordu (`localhost:5196/api/v1/auth/session`, PROXY'YI
 * HIC GORMEDEN). Staging/production'da GORULMEZ cunku oradaki baseUrl
 * MUTLAK'tir (bos yol, silinecek bir sey yok) — yalniz YEREL gelistirmede,
 * TAM DA bu depoda 2 yuzeyin kurdugu proxy deseninde patlar. Cozum: gorece
 * baseUrl'in KENDI yol parcasi, tek bir metin BIRLESTIRMESIYLE korunur,
 * sonra TEK URL cozumlemesi yapilir (ic ice iki `new URL()` DEGIL).
 */
function resolveRequestUrl(path: string, baseUrl: string): URL {
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(baseUrl)
  if (isAbsolute) return new URL(path, baseUrl)

  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const normalizedBase = baseUrl.replace(/\/$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return new URL(`${normalizedBase}${normalizedPath}`, origin)
}

/** Returned asset references cannot choose another host or escape a dev proxy prefix. */
function validateAssetRequestPath(path: string): void {
  const invalid = () => new TypeError('Asset requests require a relative API path without traversal')
  if (!path || /[\x00-\x20\x7f\\#]/.test(path) || /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(path)) throw invalid()
  const pathname = path.split('?')[0] ?? ''
  let decoded: string
  try { decoded = decodeURIComponent(pathname) } catch { throw invalid() }
  if (/%(?:2f|5c|25)/i.test(pathname) || /[\x00-\x20\x7f\\]/.test(decoded)
    || decoded.split('/').some(segment => segment === '.' || segment === '..')) throw invalid()
}

async function readBlobResponse(response: Response, resultContract: boolean): Promise<Blob> {
  const mediaType = response.headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase() ?? ''
  if (!resultContract && !mediaType.endsWith('/json') && !mediaType.endsWith('+json')) return response.blob()

  // JSON cannot be successful binary data. Still preserve semantic errors, so
  // HTTP 200 Result failures never become a downloadable "successful" Blob.
  let body: unknown
  try { body = await response.json() } catch (error) {
    if (!(error instanceof SyntaxError)) throw error // Preserve native abort/network failures.
    throw new ApiError('Invalid binary response', response.status, 'INVALID_RESPONSE')
  }
  if (resultContract || isRecord(body) && 'httpStatus' in body && 'validationErrors' in body) {
    adaptAppKitResult(body, response.status)
    throw new ApiError('Expected binary response', response.status, 'INVALID_RESPONSE')
  }
  throw new ApiError('Expected binary response', response.status,
    isRecord(body) ? readString(body.error) ?? readString(body.errorCode) ?? 'INVALID_RESPONSE' : 'INVALID_RESPONSE',
    isRecord(body) ? readString(body.error_description) ?? readString(body.detail) ?? readString(body.errorMessage) : undefined)
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const { baseUrl, tenantId } = options

  async function request<T>(path: string, requestOptions: ApiRequestOptions = {}): Promise<T> {
    const body = requestOptions.body
    const multipart = typeof FormData !== 'undefined' && body instanceof FormData
    const binary = requestOptions.responseType === 'blob'
    if (multipart || binary) {
      validateAssetRequestPath(path)
      requestOptions.signal?.throwIfAborted()
    }
    const response = await fetch(resolveRequestUrl(path, baseUrl), {
      method: requestOptions.method ?? 'GET',
      headers: buildHeaders(tenantId, requestOptions.headers, multipart, binary),
      body: multipart ? body : body === undefined ? undefined : JSON.stringify(body),
      // Asset references must not redirect a credentialed request to another origin.
      ...(multipart || binary ? { redirect: 'error' as const } : {}),
      // Oturum cerezi burada gider — token asla JS'e sizmaz.
      credentials: 'include',
      signal: requestOptions.signal,
    })

    if (!response.ok) {
      const details = await extractErrorDetails(response)
      throw new ApiError(
        `API istegi basarisiz: ${response.status} ${response.statusText}`,
        response.status,
        details.code,
        details.description,
        undefined,
        details.requiredCapability,
        details.source,
      )
    }

    if (response.status === 204) {
      return undefined as T
    }

    if (binary) return await readBlobResponse(response, requestOptions.responseContract === 'appkit-result') as T

    const responseBody: unknown = await response.json()
    return (requestOptions.responseContract === 'appkit-result'
      ? adaptAppKitResult(responseBody, response.status) : responseBody) as T
  }

  return { baseUrl, tenantId, request }
}
